import { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import {
  createPromiseRequest,
  getLmsManagerRequestParameters,
  LmsMode,
} from "@/components/lms/lms.utils";
import LmsCustomIntervalFields, {
  buildCustomIntervalPayload,
  getInvalidCustomIntervalTargets,
  getDefaultCustomIntervalState,
  LmsCustomIntervalValidationTarget,
  LmsCustomIntervalState,
} from "@/components/lms/lmsCustomIntervalFields";
import styles from "@/components/lms/lmsEnrolmentCreateEditModals.module.css";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import useFormSubmission from "@/helper/customHooks/useFormSubmission";

interface Props {
  open: boolean;
  enrolment?: any | null;
  mode?: LmsMode;
  selectedCompanySubjectId?: string | number;
  onClose: () => void;
  onCompleted: () => void;
}

const buildInitialIntervalState = (enrolment: any): LmsCustomIntervalState => {
  const hasCustomCompletion =
    enrolment?.inherited_from?.completion_interval_in_days === "enrolment";
  const hasCustomValidity =
    enrolment?.inherited_from?.validity_interval_in_days === "enrolment";

  return {
    useCustomIntervals: hasCustomCompletion || hasCustomValidity,
    completionIntervalInDays:
      hasCustomCompletion && enrolment?.completion_interval_in_days != null
        ? String(enrolment.completion_interval_in_days)
        : "",
    validityIntervalInDays:
      hasCustomValidity && enrolment?.validity_interval_in_days != null
        ? String(enrolment.validity_interval_in_days)
        : "",
  };
};

export default function LmsEnrolmentIntervalEditModal({
  open,
  enrolment,
  mode = "administrator",
  selectedCompanySubjectId,
  onClose,
  onCompleted,
}: Props) {
  const { t } = useTranslation();
  const isCompleted = enrolment?.status === "completed";
  const [customIntervals, setCustomIntervals] =
    useState<LmsCustomIntervalState>(getDefaultCustomIntervalState);
  const [validationErrors, setValidationErrors] = useState<
    LmsCustomIntervalValidationTarget[]
  >([]);
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<
      Record<string, never>,
      LmsCustomIntervalValidationTarget
    >({ fieldOrder: [] });
  const { handleError, clearError, errorModal } = useErrorModal(
    t("edit_enrolment")
  );

  useEffect(() => {
    if (!open || !enrolment) {
      return;
    }
    setCustomIntervals(buildInitialIntervalState(enrolment));
    setValidationErrors([]);
    clearError();
  }, [enrolment, open, clearError]);

  const handleSave = async () => {
    const invalidTargets = getInvalidCustomIntervalTargets(customIntervals);
    setValidationErrors(invalidTargets);
    if (!enrolment?.id || invalidTargets.length > 0) {
      if (invalidTargets.length > 0) {
        focusFirstInvalidField({}, invalidTargets);
      }
      return;
    }

    try {
      await createPromiseRequest({
        entity: "enrolment",
        entityId: String(enrolment.id),
        method: "put",
        parametersToRender: getLmsManagerRequestParameters(
          mode,
          selectedCompanySubjectId
        ),
        config: {
          headers: {
            "Content-Type": "application/json",
          },
        },
        submitData: buildCustomIntervalPayload(customIntervals, {
          includeNullForEmpty: true,
        }),
      });
      onCompleted();
    } catch (requestError) {
      handleError(requestError);
    }
  };
  const { handleSubmit, isSubmitting } = useFormSubmission({
    onSubmit: handleSave,
    disabled: !enrolment?.id || isCompleted,
  });

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          if (!isSubmitting) {
            onClose();
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t("edit_enrolment")}</DialogTitle>
        <form
          style={{ display: "contents" }}
          onSubmit={handleSubmit}
          noValidate
        >
          <DialogContent className={styles.dialogContent}>
            {isCompleted ? (
              <p>{t("lms_cannot_edit_completed_enrolment")}</p>
            ) : (
              <LmsCustomIntervalFields
                value={customIntervals}
                onChange={(value) => {
                  setValidationErrors([]);
                  setCustomIntervals(value);
                }}
                invalidTargets={validationErrors}
                registerCompletionTarget={registerValidationTarget(
                  "completionInterval"
                )}
                registerValidityTarget={registerValidationTarget(
                  "validityInterval"
                )}
              />
            )}
          </DialogContent>
          <DialogActions className={styles.dialogActions}>
            <GenericButton
              type="button"
              variant="outlined"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("cancel")}
            </GenericButton>
            <GenericButton
              type="submit"
              disabled={isSubmitting || !enrolment?.id || isCompleted}
            >
              {t("save")}
            </GenericButton>
          </DialogActions>
        </form>
      </Dialog>

      {errorModal}
    </>
  );
}
